import { i18n } from '@lingui/core';
import type { MessageDescriptor } from '@lingui/core';
import { msg, plural, t } from '@lingui/core/macro';
import { z } from 'zod';

import { listPageAction } from '@/shared/patterns/list-search';
import { action, can, defineScreen } from '@/shared/screen/define';
import type { ScreenContext } from '@/shared/screen/types';

import {
  CONTESTATION_TYPE_OPTIONS,
  PANEL_STATUS_VALUES,
  PIX_MED_PATH,
  STATUS_VALUES,
} from './pix-med.constants';
import { PIX_MED_STATUS } from './pix-med.enums';
import { PIX_MED_APPLIED_KEYS, PIX_MED_FILTER_KEYS } from './pix-med.wire';

// Manifesto do Pix Med Admin (U23, U42). Arquivo leve: entra no `src/app/screens.ts` e no ⌘K.
// Toda ação herda `PixMed:Read` da tela (`router.tsx:687`): o legado não tem gate próprio, nem Update.

export { PIX_MED_PATH };

/** `PixMed:Read` (`router.tsx:687`), a única permissão do recurso no catálogo. */
export const PIX_MED_READ = can('PixMed', 'Read');
const READ = PIX_MED_READ;

const optionalText = (max: number) => z.string().max(max).optional();
const digits = (max: number) =>
  z
    .string()
    .regex(new RegExp(`^\\d{0,${String(max)}}$`))
    .optional();

/** Os filtros do formulário (`PixMedAdminForm.tsx`), no formato legível da URL. */
export const pixMedSearchInput = z.object({
  contestationType: z.enum(CONTESTATION_TYPE_OPTIONS.map((option) => option.value) as ['1', '2']).optional(),
  recipientName: optionalText(200),
  registrationNumber: z
    .string()
    .regex(/^[A-Za-z0-9]{0,14}$/)
    .optional(),
  bankCode: optionalText(20),
  bankName: optionalText(200),
  counterpartyBankIspb: optionalText(20),
  agencyCode: digits(4),
  accountNumber: digits(16),
  transactionId: optionalText(200),
  startDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  status: z.enum(STATUS_VALUES).optional(),
});
export type PixMedSearchInput = z.infer<typeof pixMedSearchInput>;

/**
 * Patch da URL para uma consulta nova: troca todos os filtros aplicados e marca `consulta=1`. A página não
 * volta para a primeira (`PixMedAdminContainer.tsx:43-46`, divergência D5). `auxiliares` (a ordem dos
 * seletores mexidos, R3) só vem do formulário; o agente não manda e o fio usa a ordem da tela.
 */
export function pixMedSearchPatch(input: PixMedSearchInput, auxiliares?: string): Record<string, unknown> {
  const patch: Record<string, unknown> = Object.fromEntries(PIX_MED_APPLIED_KEYS.map((key) => [key, undefined]));
  for (const key of PIX_MED_FILTER_KEYS) {
    const value = input[key];
    if (value !== undefined && value !== '') patch[key] = value;
  }
  if (auxiliares !== undefined && auxiliares !== '') patch['auxiliares'] = auxiliares;
  return { ...patch, consulta: 1, contestacao: undefined };
}

export const pixMedSearch = action({
  id: 'pixMed.search',
  kind: 'read',
  label: msg`Pesquisar`,
  description: msg`Consulta as contestações do Pix MED com os filtros informados (todos opcionais; consulta vazia vale). contestationType: 1 realizada, 2 recebida. registrationNumber: CPF ou CNPJ só com dígitos (letras só no CNPJ alfanumérico). startDate: um único dia da transação (AAAA-MM-DD). status: o valor técnico do status. bankCode, bankName e counterpartyBankIspb vêm juntos, do seletor de banco. Troca os filtros anteriores e mantém a página atual. Não muda nada.`,
  keywords: [msg`contestação`, msg`MED`, msg`devolução`],
  permission: READ,
  input: pixMedSearchInput,
  run: ({ nav, input }) => nav.search(pixMedSearchPatch(input)),
});

export const pixMedListPage = listPageAction('pixMed.list.page');

const contestationInput = z.object({ id: z.string().min(1).max(100) });

export const pixMedOpenContestation = action({
  id: 'pixMed.openContestation',
  kind: 'draft',
  row: true,
  label: msg`Detalhes da contestação`,
  description: msg`Abre o painel de status de uma contestação dos resultados que aparecem agora, pelo id da contestação (ou pela posição da linha, quando ela não tem id). Não muda nada.`,
  permission: READ,
  input: contestationInput,
  run: ({ nav, input }) => nav.search({ contestacao: input.id }),
});

/** O que o painel manda para o PUT (`Drawer/PixMedAdminDrawerForm.tsx`): só as 4 opções do select. */
export const pixMedChangeStatusInput = contestationInput.extend({
  status: z.enum(PANEL_STATUS_VALUES),
  observation: z.string().max(4000).optional(),
});
export type PixMedChangeStatusInput = z.infer<typeof pixMedChangeStatusInput>;

/** Prévia legível (U24), a mesma do diálogo da tela. */
export function pixMedChangeStatusPreviewText(input: { readonly id: string; readonly status: string; readonly recipient?: string | undefined; readonly current?: string | undefined }) {
  const next = PIX_MED_STATUS.label(i18n, input.status) ?? input.status;
  const id = input.id;
  const who = input.recipient ? ` (${input.recipient})` : '';
  const from = input.current ? t`de "${input.current}" ` : '';
  return {
    title: t`Alterar status da contestação`,
    detail: t`A contestação ${id}${who} passa ${from}para "${next}". A mudança pode aprovar ou recusar a devolução do Pix.`,
  };
}

export const pixMedChangeStatus = action({
  id: 'pixMed.changeStatus',
  kind: 'write',
  row: true,
  label: msg`Alterar status da contestação`,
  description: msg`Muda o status de uma contestação dos resultados que aparecem agora, com uma observação opcional. Status permitidos: IN_ANALYSIS (em análise), APPROVED (aprovada), REJECTED (recusada) e DEADLINE_EXPIRED (prazo encerrado). Tem efeito financeiro: aprova ou recusa a devolução do Pix (MED). Pede a confirmação da pessoa.`,
  permission: READ,
  input: pixMedChangeStatusInput,
  preview: (input) => pixMedChangeStatusPreviewText(input),
  // A tela troca este `run` pelo que conhece a linha carregada (o PUT leva a linha inteira).
  run: () => {
    throw new Error('pixMed.changeStatus needs the Pix Med Admin screen with the query results loaded');
  },
});

export const pixMedAdminScreen = defineScreen({
  id: 'pixMed.admin',
  path: PIX_MED_PATH,
  title: msg`Pix Med`,
  description: msg`Administração do Pix MED (Mecanismo Especial de Devolução do Pix): consulta as contestações por tipo, destinatário, CPF/CNPJ, banco, agência, conta, id da transação, dia da transação e status, e muda o status de uma contestação (efeito financeiro, com confirmação).`,
  permission: READ,
  place: { icon: 'shield-check', section: 'bank-account' },
  actions: [pixMedSearch, pixMedListPage, pixMedOpenContestation, pixMedChangeStatus],
});

/** Rótulo de cada filtro, como no formulário da tela (o contexto não leva valor nenhum). */
const FILTER_LABELS: Readonly<Record<string, MessageDescriptor>> = {
  contestationType: msg`Tipo de contestação`,
  recipientName: msg`Nome do Destinatário`,
  registrationNumber: msg`CPF/CNPJ`,
  bankCode: msg`Banco`,
  bankName: msg`Banco`,
  counterpartyBankIspb: msg`Banco`,
  agencyCode: msg`Agência`,
  accountNumber: msg`Conta`,
  transactionId: msg`Id da Transação`,
  startDate: msg`Data da Transação`,
  status: msg`Status da contestação`,
};

/** O que o agente lê da tela (U24, U46): se já houve consulta, contagem, filtros pelo rótulo e o painel aberto, sem dado pessoal. */
export function pixMedAdminContext(input: {
  readonly searched: boolean;
  readonly total: number | undefined;
  readonly filters: readonly string[];
  readonly open: string | undefined;
}): ScreenContext {
  const total = input.total;
  const state = !input.searched
    ? t`Nenhuma consulta feita ainda.`
    : total === undefined
      ? t`Carregando os resultados.`
      : plural(total, { one: '# contestação encontrada.', other: '# contestações encontradas.' });
  const labels = [...new Set(input.filters.map((key) => (FILTER_LABELS[key] ? i18n._(FILTER_LABELS[key]) : key)))];
  const filterList = labels.join(', ');
  const contestation = input.open ?? '';
  return {
    summary: [
      t`Pix MED.`,
      state,
      labels.length > 0 ? t`Filtros: ${filterList}.` : '',
      input.open === undefined ? '' : t`Painel de status aberto para a contestação ${contestation}.`,
    ]
      .filter(Boolean)
      .join(' '),
    fields: { searched: input.searched, total: input.total ?? null, open: input.open ?? null },
    sensitivity: 'internal',
  };
}
